import type { CategoryRuleTarget, ReviewQueueItem } from '@/types/category-budget';

/**
 * One anchor a category rule can be created on. The two cover different rows, which is
 * why this is a choice and not a formatting preference: the merchant anchor follows the
 * curated identity through descriptor drift and merges, the descriptor anchor matches one
 * text. `priorCount` therefore belongs to the anchor, not to the transaction — the
 * "aplicar também aos N anteriores" line describes whichever one is selected.
 */
export interface RuleAnchorOption {
  kind: CategoryRuleTarget;
  /** What the rule will be named after: the curated brand, or the transaction's text. */
  label: string;
  priorCount: number;
  /**
   * How many descriptors this anchor covers, and a few of them. Merchant-only: the
   * descriptor anchor covers exactly one by definition, and its label already names it.
   * Evidence beats prose here — the reach is the whole reason the two anchors differ.
   */
  descriptorCount?: number;
  sampleDescriptors?: string[];
}

/**
 * What a confirm/categorize call carries about the rule the user asked for. The anchor
 * rides along because the user was shown it: omitting it would hand the decision back to
 * the server's automatic choice, which is the silence this selector exists to end.
 */
export interface RuleSaveOptions {
  createRule: boolean;
  applyToMerchant: boolean;
  /** Undefined where no anchor was shown at all — then the server's choice is the only
   *  one anyone made, and overriding it from the client would be a guess. */
  ruleAnchor?: CategoryRuleTarget;
}

/**
 * The anchors a transaction can actually mint a rule on, best first.
 *
 * The descriptor is always available; the merchant only when the API surfaced a curated
 * identity (`establishment`), which it populates for active identities alone. So a
 * one-element result is the normal case, not a degraded one — a person counterparty has
 * no `merchants` row by construction. Callers render a selector only for two options and
 * otherwise show nothing at all: an anchor that cannot be picked is omitted, never
 * offered disabled.
 */
export function buildRuleAnchors(item: {
  description: string;
  merchant?: string | null;
  priorCount: number;
  establishment?: ReviewQueueItem['establishment'];
  merchantPriorCount?: number;
}): RuleAnchorOption[] {
  const descriptor: RuleAnchorOption = {
    kind: 'descriptor',
    // The provider's merchant string when it has one, else the description — the fallback
    // chain the rule copy has always used, and the accurate one: the server builds the
    // descriptor key from the SAME field (`name:<merchantName>`, falling back to
    // `desc:<description>`). This is the only string that names the descriptor anchor;
    // showing the raw description beside it would suggest the rule matches that text,
    // which it does not when a merchant name (or a CNPJ) produced the key.
    label: item.merchant ?? item.description,
    priorCount: item.priorCount,
  };

  if (!item.establishment) return [descriptor];

  return [
    {
      kind: 'merchant',
      label: item.establishment.brandName,
      priorCount: item.merchantPriorCount ?? 0,
      descriptorCount: item.establishment.descriptorCount ?? 0,
      sampleDescriptors: item.establishment.sampleDescriptors ?? [],
    },
    descriptor,
  ];
}

/**
 * Descriptors the chips do NOT show. Never negative: the sample is capped server-side, so
 * a count that lags behind it (a merge landing between two reads) must read as "no more"
 * rather than as a negative remainder.
 */
export function remainingDescriptors(option: RuleAnchorOption): number {
  return Math.max(0, (option.descriptorCount ?? 0) - (option.sampleDescriptors?.length ?? 0));
}

/**
 * The option a chosen kind resolves to. Falls back to the first (recommended) option
 * rather than throwing: the identity can be demoted or merged between two renders, and
 * losing the user's category over a vanished radio button helps nobody. Undefined only
 * where the caller offers no rule creation at all.
 */
export function resolveRuleAnchor(
  options: RuleAnchorOption[],
  kind: CategoryRuleTarget | null,
): RuleAnchorOption | undefined {
  return options.find((o) => o.kind === kind) ?? options[0];
}
